import clsx from "clsx";
import { useState, useRef, useEffect, useCallback } from "react";

interface Props extends React.SelectHTMLAttributes<HTMLSelectElement>{
  id?: string;
  name?: string;
  placeholder?: string;
  items: { id: any; name: any; image?: string;  }[];
  Icon?: any;
  required?:boolean;
  defaultValue?:any;
  value?: any;
  className?: string;
  showImages?: boolean;
};

export default function ({ 
  id, 
  name, 
  placeholder, 
  items, 
  Icon, 
  required,
  defaultValue,
  value,
  className,
  showImages = false,
  ...rest
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(defaultValue || value || "");
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const selectedItem = items.find(item => item.id === selectedValue);
  
  // Filter items based on search query
  const filteredItems = items.filter(item => 
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery("");
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!isOpen) return;

    // Clear search timeout when user types
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Handle search by typing
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      const newSearchQuery = searchQuery + event.key.toLowerCase();
      setSearchQuery(newSearchQuery);
      
      // Find first item that starts with the search query
      const matchingIndex = filteredItems.findIndex(item => 
        item.name.toLowerCase().startsWith(newSearchQuery)
      );
      
      if (matchingIndex !== -1) {
        setHighlightedIndex(matchingIndex);
        // Scroll to the highlighted item
        const optionElement = dropdownRef.current?.querySelector(`[data-index="${matchingIndex}"]`);
        optionElement?.scrollIntoView({ block: 'nearest' });
      }

      // Clear search query after 1 second of inactivity
      searchTimeoutRef.current = setTimeout(() => {
        setSearchQuery("");
        // Reset highlight to selected item when search clears
        const selectedIndex = filteredItems.findIndex(item => item.id === selectedValue);
        setHighlightedIndex(selectedIndex);
      }, 1000);
    }

    // Handle arrow keys
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex(prev => 
        prev < filteredItems.length - 1 ? prev + 1 : 0
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex(prev => 
        prev > 0 ? prev - 1 : filteredItems.length - 1
      );
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (highlightedIndex >= 0 && filteredItems[highlightedIndex]) {
        handleItemClick(filteredItems[highlightedIndex]);
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery("");
      setHighlightedIndex(-1);
    }
  }, [isOpen, searchQuery, filteredItems, highlightedIndex, selectedValue]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }
  }, [isOpen, handleKeyDown]);

  const handleItemClick = (item: any) => {
    setSelectedValue(item.id);
    setIsOpen(false);
    setSearchQuery("");
    setHighlightedIndex(-1);
    
    // Trigger change event for form handling
    const event = new Event('change', { bubbles: true });
    const selectElement = document.getElementById(id || '') as HTMLSelectElement;
    if (selectElement) {
      selectElement.value = item.id;
      selectElement.dispatchEvent(event);
    }
  };

  // Handle dropdown open - scroll to selected item
  const handleDropdownOpen = () => {
    setIsOpen(true);
    setSearchQuery(""); // Clear any search query when opening
    
    // Find the index of the currently selected item in the full items list
    const selectedIndex = items.findIndex(item => item.id === selectedValue);
    if (selectedIndex !== -1) {
      // Find the same item in filtered items (should be the same since we cleared search)
      const filteredIndex = filteredItems.findIndex(item => item.id === selectedValue);
      setHighlightedIndex(filteredIndex);
      
      // Scroll to the selected item after a brief delay to ensure DOM is updated
      setTimeout(() => {
        const optionElement = dropdownRef.current?.querySelector(`[data-index="${filteredIndex}"]`);
        optionElement?.scrollIntoView({ block: 'nearest' });
      }, 10);
    } else {
      setHighlightedIndex(-1);
    }
  };

  if (showImages) {
    return (
      <div className={clsx("relative", className)} ref={dropdownRef}>
        {/* Hidden select for form submission */}
        <select
          {...rest}
          id={id}
          name={name}
          value={selectedValue}
          required={required}
          className="sr-only"
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {items.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>

        {/* Custom dropdown */}
        <div
          className={clsx(
            "peer bg-white block w-full cursor-pointer rounded-md border border-gray-200 py-2 pl-10 text-sm outline-2 placeholder:text-gray-500",
            {
              "pl-10": Icon != null,
              "pl-2": Icon == null,
            }
          )}
          onClick={() => isOpen ? setIsOpen(false) : handleDropdownOpen()}
          onFocus={() => {
            // Handle focus from label click
            if (!isOpen) {
              handleDropdownOpen();
            }
          }}
          tabIndex={0}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-labelledby={id ? `${id}-label` : undefined}
        >
          <div className="flex items-center gap-2">
            {selectedItem && (selectedItem.image ) && (
              <img 
                src={selectedItem.image } 
                alt={selectedItem.name}
                className="w-5 h-5 object-cover rounded"
              />
            )}
            <span>{selectedItem ? selectedItem.name : placeholder}</span>
          </div>
        </div>

        {Icon && (
          <Icon className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-500" />
        )}

        {/* Dropdown options */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
            {filteredItems.map((item, index) => (
              <div
                key={item.id}
                data-index={index}
                className={clsx(
                  "flex items-center gap-2 px-3 py-2 cursor-pointer",
                  {
                    "bg-blue-100": highlightedIndex === index,
                    "hover:bg-gray-100": highlightedIndex !== index,
                  }
                )}
                onClick={() => handleItemClick(item)}
              >
                {(item.image ) && (
                  <img 
                    src={item.image } 
                    alt={item.name}
                    className="w-5 h-5 object-cover rounded"
                  />
                )}
                <span>{item.name}</span>
              </div>
            ))}
            {filteredItems.length === 0 && (
              <div className="px-3 py-2 text-gray-500 text-sm">
                No items found
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Original select for backward compatibility
  return (
    <div className={clsx("relative",className)}>
      <select
      {...rest}
        id={id}
        name={name}
        className={clsx(
          "peer block w-full cursor-pointer rounded-md border border-gray-200 py-2 pl-10 text-sm outline-2 placeholder:text-gray-500",
          {
            "pl-10": Icon != null,
            "pl-2": Icon == null,
          }
        )}
        defaultValue={defaultValue||""}
        required={required}
        value={value}
        onKeyDown={(e) => {
          // Basic keyboard navigation for regular select
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            const select = e.target as HTMLSelectElement;
            const options = Array.from(select.options);
            const currentIndex = options.findIndex(option => option.selected);
            
            if (e.key === 'ArrowDown') {
              const nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
              select.selectedIndex = nextIndex;
            } else {
              const prevIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
              select.selectedIndex = prevIndex;
            }
            
            // Trigger change event
            const changeEvent = new Event('change', { bubbles: true });
            select.dispatchEvent(changeEvent);
          }
        }}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {items.map((item) => (
          <option key={item.id} value={item.id}>
            {item.name}
          </option>
        ))}
      </select>
      {Icon ? (
        <Icon className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-500" />
      ) : null}
    </div>
  );
}
